/**
 * EffectifsJourPage — vue consolidée « effectifs attendus du jour » agrégeant
 * cantine + périscolaire (matin/soir) + ALSH + petite enfance. Accessible à tout
 * le personnel scolaire (agent, manager, maire, admin). Affiche l'effectif
 * attendu et la liste nominative pointée (présents / absents / à venir), avec un
 * export Excel unifié.
 */
import { useEffect, useState } from 'react';
import { Users, Download, CalendarDays } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import type { EffectifsJourResult } from '@/lib/api';
import { Card, Alert, Badge, Button } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';

const STATUT_BADGE: Record<string, { label: string; variant: 'success' | 'urgent' | 'neutral' | 'info' | 'warning' }> = {
  present: { label: 'Présent', variant: 'success' },
  absent: { label: 'Absent', variant: 'urgent' },
  absent_justifie: { label: 'Absent justifié', variant: 'warning' },
  reserve: { label: 'Attendu', variant: 'info' },
};

export function EffectifsJourPage() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<EffectifsJourResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const r = await api.scolaire.effectifsJour.get(date);
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [date]);

  async function exporter() {
    try { await api.scolaire.effectifsJour.excel(date); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Export impossible'); }
  }

  return (
    <ScolaireLayout activeTab="effectifs-jour">
      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-display text-2xl font-semibold tracking-r-tight text-ink">
              <Users className="h-6 w-6 text-cantine" aria-hidden="true" /> Effectifs du jour
            </h1>
            <p className="mt-1 text-sm text-ink-muted">Cantine, périscolaire, ALSH et petite enfance regroupés, avec le pointage.</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label htmlFor="ej-date" className="mb-1 flex items-center gap-1 text-xs font-medium text-ink-muted">
                <CalendarDays className="h-3.5 w-3.5" /> Date
              </label>
              <input id="ej-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="h-10 rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" />
            </div>
            <Button variant="cantine" onClick={() => void exporter()}>
              <Download className="mr-1 h-4 w-4" /> Export Excel
            </Button>
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {loading ? (
          <Card variant="glass" padded>
            <div className="py-10 text-center">
              <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-t-transparent"
                style={{ borderColor: 'oklch(var(--c-cantine))', borderTopColor: 'transparent' }} role="status" aria-label="Chargement" />
            </div>
          </Card>
        ) : !data || data.sections.length === 0 ? (
          <Card variant="glass" padded>
            <p className="py-6 text-center text-sm text-ink-subtle">Aucun effectif attendu ce jour.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Synthèse */}
            <Card variant="glass" padded>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                  <div className="text-xs text-ink-subtle">Total attendus</div>
                  <div className="text-2xl font-semibold tabular-nums text-ink">{data.totalAttendus}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-subtle">Total présents</div>
                  <div className="text-2xl font-semibold tabular-nums text-success">{data.totalPresents}</div>
                </div>
              </div>
            </Card>

            {/* Une carte par service */}
            {data.sections.map((s) => (
              <Card key={s.cle} className="overflow-hidden p-0">
                <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
                  <h2 className="font-display text-sm font-semibold text-ink">{s.libelle}</h2>
                  <Badge variant="info" size="sm">{s.attendus} attendu{s.attendus > 1 ? 's' : ''}</Badge>
                  <Badge variant="success" size="sm">{s.presents} présent{s.presents > 1 ? 's' : ''}</Badge>
                  <span className="ml-auto text-xs text-ink-subtle">{s.pointes} pointé{s.pointes > 1 ? 's' : ''}</span>
                </div>
                {s.lignes.length === 0 ? (
                  <p className="px-4 py-4 text-center text-sm text-ink-subtle">Aucun enfant.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {s.lignes.map((l, i) => {
                      const b = STATUT_BADGE[l.statut] ?? { label: l.statut, variant: 'neutral' as const };
                      return (
                        <li key={`${s.cle}-${i}`} className="flex items-center gap-3 px-4 py-2">
                          <span className="min-w-0 flex-1 text-sm text-ink">
                            {l.nom} {l.prenom}
                            {l.detail && <span className="ml-2 text-xs text-ink-subtle">{l.detail}</span>}
                          </span>
                          <Badge variant={b.variant} size="sm">{b.label}</Badge>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </ScolaireLayout>
  );
}
